import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { getUserData } from './utils/userService';
import { User, Mail, Phone, LogOut, Shield, Calendar, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLandingPageCleanup } from './contexts/LandingPageContext';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { isCleaningUp, cleanupComplete } = useLandingPageCleanup();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const data = await getUserData(currentUser.uid);
          setUserData(data);
        } catch (error) {
          console.error('Kullanıcı verisi alınırken hata:', error);
        }
      } else {
        // Kullanıcı giriş yapmamış, login sayfasına yönlendir
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    if (window.confirm('Çıkış yapmak istediğinize emin misiniz?')) {
      setIsSigningOut(true);
      try {
        await signOut(auth);
        navigate('/');
      } catch (error) {
        console.error('Çıkış yapılırken hata:', error);
        alert('Çıkış yapılırken bir hata oluştu. Lütfen tekrar deneyiniz.');
      } finally {
        setIsSigningOut(false);
      }
    }
  };

  // CSS cleanup tamamlanana kadar loading göster
  if (isCleaningUp || !cleanupComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="text-white text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p className="text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="text-white text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p className="text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <header className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-xl font-bold text-white hover:text-purple-400 transition-colors">
              Yaşasın Hamileyim
            </Link>
            <Link
              to="/"
              className="text-gray-300 hover:text-white transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Ana Sayfa
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700/50 p-8">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Profilim</h1>
            <p className="text-gray-400">Hesap bilgileriniz ve ayarlar</p>
          </div>

          {/* User Info Card */}
          <div className="bg-gray-700/50 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase() || 'U'}
              </div>

              {/* User Details */}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-2">
                  {user.displayName || user.email || 'Kullanıcı'}
                </h2>
                
                {/* Email */}
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-300">{user.email}</span>
                </div>

                {/* Phone Number */}
                {userData?.phoneNumber && (
                  <div className="flex items-center gap-3 mb-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-300">{userData.phoneNumber}</span>
                  </div>
                )}

                {/* Premium Status */}
                <div className="flex items-center gap-3 mt-4">
                  {userData?.isPremium ? (
                    <>
                      <Shield className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-semibold">Premium Üye</span>
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-400">Standart Üye</span>
                      <Link
                        to="/checkout"
                        className="ml-4 text-purple-400 hover:text-purple-300 transition-colors text-sm font-semibold"
                      >
                        Premium'a Geç →
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Account Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-700/50 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-5 h-5 text-purple-400" />
                <h3 className="text-gray-300 font-semibold">Üyelik Tarihi</h3>
              </div>
              <p className="text-white text-lg">
                {userData?.createdAt?.toDate 
                  ? new Date(userData.createdAt.toDate()).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : 'Bilinmiyor'}
              </p>
            </div>

            <div className="bg-gray-700/50 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <User className="w-5 h-5 text-purple-400" />
                <h3 className="text-gray-300 font-semibold">Tamamlanan Dersler</h3>
              </div>
              <p className="text-white text-lg">
                {userData?.completedLessons?.length || 0} ders
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            {!userData?.isPremium && (
              <Link
                to="/checkout"
                className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 text-center"
              >
                Premium'a Geç
              </Link>
            )}
            
            <button
              onClick={handleSignOut}
              disabled={isSigningOut}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                isSigningOut
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-500/20 hover:shadow-red-500/30'
              }`}
            >
              {isSigningOut ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Çıkış yapılıyor...
                </>
              ) : (
                <>
                  <LogOut className="w-5 h-5" />
                  Çıkış Yap
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;

